import React from 'react';
import { useForm } from 'react-hook-form';

import { ReactComponent as AddIcon } from '@assets/svg/add.svg';

import Button from '@components/uikit/Button/Button';
import Input from '@components/uikit/Input/Input';
import Select from '@components/uikit/Select/Select';

import { MODAL_NAMES } from '@constants/modalRenderers';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import type { IAssetTracking } from '@model/assetTracking';
import type { IGPSTracker } from '@model/gpsTracker';

import { useGPSAssetSaveMutation } from '@pages/GPSAssetTracking/services/useGPSAssetSaveMutation';
import {
  selectAssetCreatedTracker,
  useAssetTrackerActions,
} from '@pages/GPSAssetTracking/store/useAssetTrackerStore';

import { useModalActions } from '@store/useModalStore';

import ModalHeader from '../ModalHeader/ModalHeader';
import {
  ASSET_TRACKING_FIELDS,
  type IFormAssetTracking,
  assetTrackingValues,
  schemaAssetTracking,
} from './constants/schemaAssetTracking';
import { useGPSTrackerSelectQuery } from './services/useGPSTrackerSelectQuery';
import style from './style.module.scss';

interface IProps {
  asset: IAssetTracking;
  gpsTracker: IGPSTracker;
}

const CreateEditAssetTrackingModal: React.FC<IProps> = ({ asset, gpsTracker }) => {
  const { t } = useTranslation();

  const { setModalClose, setSecondaryModal } = useModalActions();
  const { setAssetCreatedTracker } = useAssetTrackerActions();
  const createdTracker = selectAssetCreatedTracker();

  const isDisabledTracker = !asset && gpsTracker;

  const { data: gpsTrackerSelectData, isFetching } = useGPSTrackerSelectQuery();
  const { mutate: gpsAssetCreateMutation, isLoading: isGpsAssetCreateLoading } = useGPSAssetSaveMutation({
    gpsAssetId: asset?.id,
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<IFormAssetTracking>({
    mode: 'all',
    resolver: schemaAssetTracking,
    //@ts-ignore
    values: assetTrackingValues(asset, createdTracker || gpsTracker),
    resetOptions: {
      keepDirtyValues: !!(createdTracker || gpsTracker),
    },
  });

  const handleClose = () => {
    reset();
    setAssetCreatedTracker(null);
    setModalClose();
  };

  const handleSave = handleSubmit((data) => {
    gpsAssetCreateMutation(
      {
        ...data,
        [ASSET_TRACKING_FIELDS.GPS_TRACKER_ID]: data.gpsTrackerId.id,
      },
      {
        onSuccess: () => {
          showToast({ variant: 'success', message: t('Your changes have been saved.') });

          handleClose();
        },
      },
    );
  });

  const handleOpenAddGPSModal = () => {
    setSecondaryModal({ name: MODAL_NAMES.createEditGpsTrackerModal });
  };

  const emptySelectData = (searchable?: boolean) => {
    if (!searchable) {
      return <div className={style.emptySelectData}>{t('No available GPS tracker.')}</div>;
    }

    return <div className={style.emptySelectData}>{t('No matches were found.')}</div>;
  };

  return (
    <div className={style.createAssetTrackingModal}>
      <ModalHeader
        title={t(asset?.id ? 'Edit asset tracking' : 'Add asset tracking')}
        onClose={handleClose}
      />
      <div className={style.modalBody}>
        <div className={style.twoFields}>
          <Input name={ASSET_TRACKING_FIELDS.ASSET_TAG} control={control} label={t('Asset Tag')} required />
          <Input
            name={ASSET_TRACKING_FIELDS.EXTERNAL_ASSET_ID}
            control={control}
            label={t('Asset Tag ID')}
            required
          />
        </div>
        <div className={style.gpsTrackerField}>
          <Select
            name={ASSET_TRACKING_FIELDS.GPS_TRACKER_ID}
            control={control}
            label={t('Select GPS Tracker')}
            options={gpsTrackerSelectData || []}
            itemKeys={{ valueFieldKey: 'id', labelFieldKey: 'fullName' }}
            loadingList={isFetching}
            disabled={!!isDisabledTracker}
            searchable={true}
            noDataRenderer={({ props: { searchable } }) => emptySelectData(searchable)}
            required
          />
          {!isDisabledTracker && (
            <div className={style.addButtonContainer}>
              <Button
                text={t('Add GPS Tracker')}
                onClick={handleOpenAddGPSModal}
                type="secondary"
                leftElement={<AddIcon className={style.addIcon} />}
              />
            </div>
          )}
        </div>
      </div>
      <div className={style.modalFooter}>
        <Button type="secondary" text={t('Cancel')} onClick={handleClose} />
        <Button
          type="primary"
          variant={'medium'}
          text={t('Submit')}
          disabled={!isDirty || isGpsAssetCreateLoading}
          onClick={handleSave}
        />
      </div>
    </div>
  );
};

export default CreateEditAssetTrackingModal;
